import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import languageService from "./services/languageService";
import { getAppTokens } from "./theme/tokens";

import "./styles/animations.css";

const queryClient = new QueryClient();

const App = () => {
  const darkElectricBlue = "#38BDF8";
  const [locale, setLocale] = useState();
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    localStorage.getItem("primaryColorDark") ||
    "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        (() => {
          const resolvedPrimary =
            mode === "light" ? primaryColorLight : darkElectricBlue;
          const tokens = getAppTokens(
            mode,
            resolvedPrimary
          );
          return {
          appTokens: tokens,
          // Scrollbar styles melhorados mas usando cores do tema
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor:
                mode === "light" ? primaryColorLight : resolvedPrimary,
              borderRadius: "4px", // Bordas arredondadas
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: mode === "light" ? "#f5f5f5" : "rgba(15,23,42,0.72)",
              borderRadius: "4px",
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#E0E0E0" : "#404040",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: mode === "light" ? "#BDBDBD" : "#505050",
              }
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

          palette: {
            type: mode,
            primary: {
              main: resolvedPrimary,
              light: mode === "light"
                ? `${primaryColorLight}80`
                : `${resolvedPrimary}80`,
              dark: mode === "light"
                ? `${primaryColorLight}CC`
                : `${resolvedPrimary}CC`,
              contrastText: "#ffffff",
            },
            background: {
              default: tokens.colors.surfaceAlt,
              paper: tokens.colors.surfaceElevated
            },
            divider: tokens.colors.border,
            text: {
              primary: tokens.colors.text,
              secondary: tokens.colors.textMuted,
            },
            textPrimary: tokens.colors.text,
            borderPrimary:
              resolvedPrimary,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: tokens.colors.text,
            tabHeaderBackground: mode === "light" ? "#EEE" : "rgba(22,32,51,0.82)",
            optionsBackground: mode === "light" ? "#fafafa" : "rgba(15,23,42,0.82)",
            fancyBackground: mode === "light"
              ? "#fafafa"
              : "radial-gradient(1200px 680px at 0% 0%, rgba(56,189,248,0.12), transparent 55%), radial-gradient(900px 520px at 100% 10%, rgba(59,130,246,0.1), transparent 60%), linear-gradient(180deg, rgba(15,23,42,0.98), rgba(17,28,49,0.98))",
            total: mode === "light" ? "#fff" : "rgba(22,32,51,0.92)",
            messageIcons: mode === "light" ? "grey" : "#CBD5E1",
            inputBackground: mode === "light" ? "#FFFFFF" : "rgba(22,32,51,0.86)",
            barraSuperior: mode === "light" ? primaryColorLight : resolvedPrimary,
          },

          typography: {
            fontFamily: [
              'Inter',
              '-apple-system',
              'BlinkMacSystemFont',
              '"Segoe UI"',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif',
            ].join(','),
            h1: {
              fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h2: {
              fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h3: {
              fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h4: {
              fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h5: {
              fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h6: {
              fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            button: {
              fontWeight: 600,
              textTransform: 'none',
              letterSpacing: '0.025em',
            },
          },

          shape: {
            borderRadius: tokens.radius.md,
          },
          overrides: {
            // Botões usando cor do tema
            MuiButton: {
              root: {
                borderRadius: tokens.radius.md,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              },
              contained: {
                boxShadow: tokens.shadows.sm,
                '&:hover': {
                  boxShadow: tokens.shadows.md,
                }
              }
            },

            MuiContainer: {
              root: {
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                maxWidth: 'none !important',
                width: '100% !important',
              },
              maxWidthLg: {
                maxWidth: 'none !important',
              },
              maxWidthMd: {
                maxWidth: 'none !important',
              },
              maxWidthSm: {
                maxWidth: 'none !important',
              },
              maxWidthXl: {
                maxWidth: 'none !important',
              },
              maxWidthXs: {
                maxWidth: 'none !important',
              },
            },
          
            // Papers com largura controlada por contexto
            MuiPaper: {
              root: {
                backgroundImage: 'none',
                // Aplicar width 100% apenas em contextos específicos
                '&.main-content-paper': {
                  marginLeft: 0,
                  marginRight: 0,
                  width: '100%',
                },
              },
              rounded: {
                borderRadius: tokens.radius.lg,
              },
              elevation1: {
                boxShadow: tokens.shadows.sm,
              },
              elevation2: {
                boxShadow: tokens.shadows.md,
              },
              elevation3: {
                boxShadow: tokens.shadows.lg,
              }
            },

            // Proteção específica para menus - sem !important
            MuiMenu: {
              paper: {
                width: 'auto',
                maxWidth: '400px',
                minWidth: '180px',
                marginTop: 8, // Usar valor fixo ao invés de theme.spacing(1)
              }
            },

            // Proteção específica para popovers
            MuiPopover: {
              paper: {
                width: 'auto',
                maxWidth: '500px',
              }
            },
            
            // Proteção para diálogos
            MuiDialog: {
              paper: {
                margin: 16, // Usar valor fixo ao invés de theme.spacing(2)
                width: 'calc(100% - 64px)',
                maxWidth: '600px',
              }
            },

            // Inputs melhorados
            MuiTextField: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: tokens.radius.md,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: tokens.colors.borderStrong,
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? primaryColorLight : resolvedPrimary,
                      borderWidth: 2,
                    }
                  }
                }
              }
            },

            // Tabs usando cor do tema
            MuiTab: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: mode === "light"
                    ? `${primaryColorLight}08`
                    : `${resolvedPrimary}12`,
                },
                '&.Mui-selected': {
                  color: mode === "light" ? primaryColorLight : resolvedPrimary,
                }
              }
            },

            // Drawer sem bordas
            MuiDrawer: {
              paper: {
                border: 'none',
              }
            },

            // AppBar transparente
            MuiAppBar: {
              root: {
                boxShadow: 'none',
              }
            }
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        };
        })(),
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight, // Essas são as cores que vêm do tema dinâmico
    ]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    // Inicializar serviço de idiomas
    languageService.initialize().then(() => {
      // Migrar dados do localStorage se necessário
      languageService.migrateFromLocalStorage();
    });
  }, []);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#0000FF");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#39ACE7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(
          file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Multi100");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
        setAppName("Multi100");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DEBUG opcional: rastrear elementos que capturam cliques (ativar com localStorage.setItem('debugPointer','1'))
  useEffect(() => {
    if (localStorage.getItem('debugPointer') !== '1') return;
    const getPath = (el) => {
      try {
        const path = [];
        while (el && el.nodeType === 1 && path.length < 10) {
          const name = el.nodeName.toLowerCase();
          const id = el.id ? `#${el.id}` : '';
          const cls = el.className && typeof el.className === 'string' ? `.${el.className.split(/\s+/).slice(0,3).join('.')}` : '';
          path.unshift(`${name}${id}${cls}`);
          el = el.parentElement;
        }
        return path.join(' > ');
      } catch { return '(path-error)'; }
    };
    const onPointer = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      // eslint-disable-next-line no-console
      console.log('[debugPointer]', { x: e.clientX, y: e.clientY, target: e.target, topEl: el, path: getPath(el) });
    };
    window.addEventListener('pointerdown', onPointer, true);
    return () => window.removeEventListener('pointerdown', onPointer, true);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : darkElectricBlue
    );
  }, [primaryColorLight, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        const currentVersion = window.localStorage.getItem("frontendVersion");
        
        // Se a versão mudou, notificar o usuário
        if (currentVersion && currentVersion !== data.version) {
          console.log(`Nova versão detectada: ${data.version} (anterior: ${currentVersion})`);
          
          // Mostrar notificação para o usuário
          const updateApp = window.confirm(
            "Uma nova versão da aplicação está disponível. Deseja atualizar agora?"
          );
          
          if (updateApp) {
            window.localStorage.setItem("frontendVersion", data.version);
            window.location.reload(true); // Force reload from server
          }
        } else {
          window.localStorage.setItem("frontendVersion", data.version);
        }
      } catch (error) {
        console.log("Error fetching version data", error);
      }
    }
    
    // Verificar versão ao carregar
    fetchVersionData();
    
    // Verificar versão periodicamente (a cada 5 minutos)
    const versionCheckInterval = setInterval(fetchVersionData, 5 * 60 * 1000);
    
    return () => {
      clearInterval(versionCheckInterval);
    };
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
